import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { faArrowAltFromLeft, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { Select, Store } from '@ngxs/store';
import { iif, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
   ApplyAlternativeDeviceAddress,
   ResetAlternativeDeviceAddress,
   UserStatusAlternativeDeviceAddressChangedWithoutApplying,
} from '../../../../../store/dashboard/brick-wall/brick-behaviour-state/brickTypes/user-status-behaviour/user-status-behaviour.actions';
import { UserStatusBehaviourState } from '../../../../../store/dashboard/brick-wall/brick-behaviour-state/brickTypes/user-status-behaviour/user-status-behaviour.state';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';

@Component({
   selector: 'samweb-alternative-device-editor',
   templateUrl: './alternative-device-editor.component.html',
   styleUrls: ['./alternative-device-editor.component.scss'],
})
export class AlternativeDeviceEditorComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();

   public alternativeDeviceActiveIcon = faArrowAltFromLeft;
   public applyAlternativeDeviceIcon = WellKnownIconsEnum.CONFIRM.icon;
   public resetAlternativeDeviceIcon = WellKnownIconsEnum.CLEAR.icon;

   @Select(SamwinAcdExtensionStateSelectors.getCurrentAcdIcon)
   public deviceIcon$: Observable<IconDefinition>;

   @Select(SamwinAcdExtensionStateSelectors.isCurrentDeviceAddressAlternativeDevice)
   public isCurrentDeviceAddressAlternativeDevice$: Observable<boolean>;

   public deviceAddress$: Observable<string>;

   public currentUnappliedAlternativeDeviceAddress$: Observable<string>;
   public isUnappliedAlternativeDeviceAddress$: Observable<boolean>;

   @Input()
   public brickInstanceId: string;

   constructor(private store: Store, private translateService: TranslateService) {}

   ngOnInit(): void {
      this.currentUnappliedAlternativeDeviceAddress$ = this.store
         .select(UserStatusBehaviourState.getUnappliedAlternativeDeviceAddressFn)
         .pipe(map((fn) => fn(this.brickInstanceId)));

      this.deviceAddress$ = this.currentUnappliedAlternativeDeviceAddress$.pipe(
         switchMap((currentUnappliedAlternativeDeviceAddress: string) =>
            iif(
               () => _.isNil(currentUnappliedAlternativeDeviceAddress),
               this.store.select(SamwinAcdExtensionStateSelectors.getCurrentDeviceAddress),
               of(currentUnappliedAlternativeDeviceAddress)
            )
         )
      );

      this.isUnappliedAlternativeDeviceAddress$ = this.currentUnappliedAlternativeDeviceAddress$.pipe(
         map((x) => !_.isNil(x) && x !== this.store.selectSnapshot(SamwinAcdExtensionStateSelectors.getCurrentDeviceAddress))
      );
   }

   changeDeviceAddressUnapplied(value: string) {
      this.store.dispatch(new UserStatusAlternativeDeviceAddressChangedWithoutApplying(this.brickInstanceId, value));
   }

   applyAlternativeDeviceAddress() {
      this.store.dispatch(new ApplyAlternativeDeviceAddress(this.brickInstanceId));
   }

   resetAlternativeDeviceAddress() {
      this.store.dispatch(new ResetAlternativeDeviceAddress(this.brickInstanceId));
   }

   // When input loses focus, we wait 200ms before trying to reset the value because the loss of focus might be caused by applying changes
   debounceResetAlternativeDeviceAddress() {
      setTimeout(() => {
         // I want to do this just once, therefore I pipe just one pushed value
         this.isUnappliedAlternativeDeviceAddress$
            .pipe(
               take(1),
               tap((isUnapplied) => {
                  if (isUnapplied) {
                     const currentConfirmedDevice = this.store.selectSnapshot(SamwinAcdExtensionStateSelectors.getCurrentDeviceAddress);
                     this.store.dispatch(new UserStatusAlternativeDeviceAddressChangedWithoutApplying(this.brickInstanceId, currentConfirmedDevice));
                  }
               }),
               takeUntil(this.destroy$)
            )
            .subscribe();
      }, 200);
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
